
import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import { getTdsSectionDropdown, getRuleConditions } from '../../../../api/RuleSettingAPI';
import { fieldOptions, operatorOptions, validatePAN, validateTAN } from '../../Define/configs/ruleSettingTableConfig';
import { toDisplayDate } from '../../../../constants/Utils';
import Layout from '../../../layout/Layout';
import { useSidebar } from '../../../../contexts/SidebarContext';
import CustomInput from '../../CustomInput';
import StyledSwitch from '../../../common/StyledSwitch';
import CustomSelect from '../../CustomSelect';

// Reusable DatePicker styling configuration.
// Hoisted to module scope so it has a stable identity and does not force
// memoized condition rows to re-render on every parent update.
const getDatePickerSlotProps = (textFieldProps = {}) => ({
  textField: {
    fullWidth: true,
    size: 'small',
    InputProps: {
      sx: {
        '& .MuiInputAdornment-root .MuiIconButton-root': {
          color: 'var(--forbrand-color)',
        },
      },
    },
    ...textFieldProps,
  },
  openPickerIcon: {
    sx: {
      color: 'var(--forbrand-color)',
    },
  },
  popper: {
    sx: {
      // Global override for year picker buttons
      '& .MuiPickersYear-yearButton.Mui-selected': {
        backgroundColor: 'var(--forbrand-color) !important',
        color: '#fff !important',
      },
      '& .MuiPickersYear-yearButton.Mui-selected:hover': {
        backgroundColor: '#C12E47 !important',
      },
      '& .MuiPickersYear-yearButton:hover': {
        backgroundColor: 'rgba(217, 57, 84, 0.1) !important',
      },
    },
  },
  calendarHeader: {
    sx: {
      '& .MuiPickersCalendarHeader-root': {
        color: 'var(--forbrand-color)',
      },
      '& .MuiIconButton-root': {
        color: 'var(--forbrand-color)',
      },
    },
  },
  day: {
    sx: {
      '&.Mui-selected': {
        backgroundColor: 'var(--forbrand-color) !important',
        color: '#fff',
      },
      '&.Mui-selected:hover': {
        backgroundColor: '#C12E47 !important',
      },
      '&:hover': {
        backgroundColor: 'rgba(217, 57, 84, 0.1)',
      },
    },
  },
  month: {
    sx: {
      '&.Mui-selected': {
        backgroundColor: 'var(--forbrand-color) !important',
        color: '#fff',
      },
      '&.Mui-selected:hover': {
        backgroundColor: '#C12E47 !important',
      },
      '&:hover': {
        backgroundColor: 'rgba(217, 57, 84, 0.1)',
      },
    },
  },
  year: {
    sx: {
      '&.Mui-selected': {
        backgroundColor: 'var(--forbrand-color) !important',
        color: '#fff !important',
      },
      '&.Mui-selected:hover': {
        backgroundColor: '#C12E47 !important',
      },
      '&:hover': {
        backgroundColor: 'rgba(217, 57, 84, 0.1)',
      },
      // Additional selectors for year picker specificity
      '&.MuiPickersYear-yearButton.Mui-selected': {
        backgroundColor: 'var(--forbrand-color) !important',
        color: '#fff !important',
      },
      '&.MuiPickersYear-yearButton.Mui-selected:hover': {
        backgroundColor: '#C12E47 !important',
      },
    },
  },
});

// Precompute the operator option list per data type once, instead of
// filtering the operator array while rendering every condition row.
const NUMERIC_ONLY_OPERATORS = ['BETWEEN', 'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_OR_EQUAL_TO', 'LESS_THAN_OR_EQUAL_TO'];
const OPERATORS_WITHOUT_NUMERIC = operatorOptions.filter(op => !NUMERIC_ONLY_OPERATORS.includes(op.value));
// Operators that accept a comma-separated list of values (e.g. "A,B,C" or
// "10/12/2019,21/12/2019"). For these the value input is always a free-text
// field (even for date fields) and each entry is validated individually.
const LIST_OPERATORS = ['IN_LIST', 'NOT_IN_LIST', 'IN_LIST_PROBABLE_MATCH', 'NOT_IN_LIST_PROBABLE_MATCH'];
const DATE_DDMMYYYY_REGEX = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
// Fast lookup for field metadata (avoids fieldOptions.find per keystroke/row).
const FIELD_OPTION_BY_VALUE = fieldOptions.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

// Validate a comma-separated list of values for the IN_LIST / NOT_IN_LIST
// family of operators. Each entry is trimmed and validated individually using
// the field's own dataType/validation config. Returns an error string or null.
const validateCsvListValue = (rawValue, fieldMeta) => {
  const valueStr = String(rawValue || '').trim();
  if (!valueStr) return 'At least one value is required';

  // Split on commas, trim entries, and drop empty ones caused by trailing/
  // duplicate commas so "A,,B," is treated as ["A", "B"].
  const parts = valueStr.split(',').map(p => p.trim());
  const nonEmpty = parts.filter(p => p !== '');
  if (nonEmpty.length === 0) return 'At least one value is required';

  const dataType = fieldMeta?.dataType || 'string';
  const validation = fieldMeta?.validation;

  for (const part of nonEmpty) {
    // Always apply the field's own validation (regex + maxLength) to every
    // entry when configured — e.g. TDS Base Amount must match its numeric
    // regex (up to 12 digits, 2 decimals) for each value in the list.
    if (validation) {
      if (validation.maxLength && part.length > validation.maxLength) {
        return validation.error || `Each value must be at most ${validation.maxLength} characters`;
      }
      if (validation.regex && !validation.regex.test(part)) {
        return validation.error || `Invalid value "${part}"`;
      }
      // Field-level validation passed for this entry.
      continue;
    }

    // Fallbacks when the field has no explicit validation config.
    if (dataType === 'date') {
      if (!DATE_DDMMYYYY_REGEX.test(part)) {
        return `Invalid date "${part}". Use DD/MM/YYYY (comma separated for multiple)`;
      }
      continue;
    }

    if (dataType === 'number') {
      if (!/^[-+]?\d+(\.\d+)?$/.test(part)) {
        return `Invalid number "${part}"`;
      }
      continue;
    }
  }

  return null;
};

// A single condition row. Memoized so that editing one condition only
// re-renders that row instead of every row in the list — the main cause of
// the sluggish edit experience when a rule has many (80+) conditions.
const ConditionRow = memo(function ConditionRow({
  condition,
  index,
  showDelete,
  fieldError,
  operatorError,
  valueError,
  conditionTdsSectionOptions,
  onConditionChange,
  onRemove,
}) {
  const selectedFieldOption = FIELD_OPTION_BY_VALUE[condition.field];
  const dataType = selectedFieldOption?.dataType || 'string';
  const op = String(condition.operator).toUpperCase();
  const isValueDisabled = op === 'IS_NULL' || op === 'IS_NOT_NULL';
  const fieldLabel = selectedFieldOption?.label ? `${selectedFieldOption?.label}` : 'Value';
  const isNumericOrDate = dataType === 'number' || dataType === 'date';
  const operatorList = isNumericOrDate ? operatorOptions : OPERATORS_WITHOUT_NUMERIC;

  const isListOperator = LIST_OPERATORS.includes(op);

  const renderValueInput = () => {
    // List operators (IN_LIST / NOT_IN_LIST / *_PROBABLE_MATCH): always a
    // free-text input accepting comma-separated values, even for date fields.
    if (isListOperator) {
      const placeholder = dataType === 'date'
        ? 'Example: 10/12/2019,21/12/2019'
        : 'Enter comma separated values (e.g. A,B,C)';

      return (
        <CustomInput
          fullWidth
          label={fieldLabel}
          value={condition.value}
          onChange={(e) => {
            let value = e.target.value;
            if (dataType === 'date') {
              // Restrict to digits, slashes and commas for date lists.
              value = value.replace(/[^0-9/,]/g, '');
            } else if (dataType === 'number') {
              // Allow digits, sign, decimal and commas as the list separator.
              value = value.replace(/[^0-9.,\-+]/g, '');
            }
            onConditionChange(condition.id, 'value', value);
          }}
          error={!!valueError}
          placeholder={placeholder}
          helperText={valueError || ''}
          disabled={isValueDisabled}
        />
      );
    }

    // BETWEEN operator: special input for two colon-separated values
    if (op === 'BETWEEN') {
      const isBetweenDate = dataType === 'date';
      const placeholder = isBetweenDate
        ? 'Example: 10/12/2019:21/12/2019'
        : 'Example: 100:500';

      return (
        <CustomInput
          fullWidth
          label={fieldLabel}
          value={condition.value}
          onChange={(e) => {
            let value = e.target.value;
            if (isBetweenDate) {
              value = value.replace(/[^0-9/:]/g, '');
              const colonCount = (value.match(/:/g) || []).length;
              if (colonCount > 1) return;
            } else {
              value = value.replace(/[^0-9.:\-]/g, '');
              const colonCount = (value.match(/:/g) || []).length;
              if (colonCount > 1) return;
            }
            onConditionChange(condition.id, 'value', value);
          }}
          error={!!valueError}
          placeholder={placeholder}
          helperText={valueError || ''}
        />
      );
    }

    if (dataType === 'date') {
      return (
        <Box sx={{ minWidth: 250, flex: '0 0 auto' }}>
          <Typography sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            mb: 1,
            lineHeight: '140%'
          }}>
            {fieldLabel}
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              format="DD/MM/YYYY"
              value={condition.value ? dayjs(condition.value, 'DD/MM/YYYY') : null}
              onChange={(newValue) => {
                if (newValue) {
                  onConditionChange(condition.id, 'value', newValue.format('DD/MM/YYYY'));
                }
              }}
              disabled={isValueDisabled}
              slotProps={{
                ...getDatePickerSlotProps({
                  error: !!valueError,
                  helperText: valueError,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '2px',
                      backgroundColor: isValueDisabled ? '#f5f5f5' : 'white',
                      height: '48px',
                      '& fieldset': {
                        borderColor: valueError ? 'var(--forbrand-error)' : '#d0d0d0',
                      },
                      '&:hover fieldset': {
                        borderColor: valueError ? 'var(--forbrand-error)' : '#a0a0a0',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: valueError ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      backgroundColor: 'transparent',
                      margin: 0,
                      mt: 0.5
                    }
                  }
                })
              }}
            />
          </LocalizationProvider>
        </Box>
      );
    } else if (dataType === 'number') {
      return (
        <CustomInput
          fullWidth
          label={fieldLabel}
          type="number"
          value={condition.value}
          onChange={(e) => {
            const value = e.target.value;
            const { maxLength } = selectedFieldOption?.validation || {};
            if (maxLength && value.length > maxLength) return;
            if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
              onConditionChange(condition.id, 'value', value);
            }
          }}
          error={!!valueError}
          placeholder="Enter the respective value"
          helperText={valueError}
          disabled={isValueDisabled}
        />
      );
    } else if (dataType === 'email') {
      return (
        <CustomInput
          fullWidth
          label={fieldLabel}
          type="email"
          value={condition.value}
          onChange={(e) => {
            const value = e.target.value;
            const maxLength = selectedFieldOption?.validation?.maxLength;
            if (maxLength && value.length > maxLength) return;
            onConditionChange(condition.id, 'value', value);
          }}
          error={!!valueError}
          placeholder="Enter the respective value"
          helperText={valueError}
          disabled={isValueDisabled}
          maxLength={selectedFieldOption?.validation?.maxLength}
        />
      );
    } else if (dataType === 'tel') {
      return (
        <CustomInput
          fullWidth
          label={fieldLabel}
          type="tel"
          value={condition.value}
          onChange={(e) => {
            const value = e.target.value;
            const maxLength = selectedFieldOption?.validation?.maxLength;
            if (maxLength && value.length > maxLength) return;
            if (/^[0-9\s+\-()]*$/.test(value)) {
              onConditionChange(condition.id, 'value', value);
            }
          }}
          error={!!valueError}
          placeholder="Enter the respective value"
          helperText={valueError}
          disabled={isValueDisabled}
          maxLength={selectedFieldOption?.validation?.maxLength}
        />
      );
    } else if (dataType === 'dropdown') {
      const options = condition.field === 'TDS_SECTION'
        ? conditionTdsSectionOptions
        : selectedFieldOption?.options || [];
      return (
        <CustomSelect
          label={fieldLabel}
          fullWidth
          placeholder="Select respective value"
          options={options}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.id || opt.value}
          value={condition.value || ''}
          onChange={(e) => onConditionChange(condition.id, 'value', e.target.value)}
          error={!!valueError}
          helperText={valueError}
          disabled={isValueDisabled}
        />
      );
    }
    return (
      <CustomInput
        fullWidth
        label={fieldLabel}
        value={condition.value}
        onChange={(e) => {
          const value = e.target.value;
          const maxLength = selectedFieldOption?.validation?.maxLength;
          if (maxLength && value.length > maxLength) return;
          onConditionChange(condition.id, 'value', value);
        }}
        error={!!valueError}
        placeholder="Enter the respective Value"
        helperText={valueError}
        disabled={isValueDisabled}
        maxLength={selectedFieldOption?.validation?.maxLength}
      />
    );
  };

  return (
    <Box id={String(condition.id)} sx={{ mb: 3 }}>
      {/* Logical Operator (And / OR) */}
      {index > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <CustomSelect
              label=""
              value={condition.logicalOperator || 'And'}
              onChange={(e) => onConditionChange(condition.id, 'logicalOperator', e.target.value)}
              options={[
                { label: 'AND', value: 'And' },
                { label: 'OR', value: 'OR' },
              ]}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
          </Box>
        </Box>
      )}

      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, fontSize: '16px', color: '#333' }}
      >
        Condition {index + 1}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* Field Dropdown */}
        <CustomSelect
          label="Field"
          placeholder="Select Field"
          options={fieldOptions}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
          value={condition.field || ''}
          onChange={(e) => onConditionChange(condition.id, 'field', e.target.value)}
          error={!!fieldError}
          helperText={fieldError}
          fullWidth
        />

        {/* Operator Dropdown */}
        <CustomSelect
          fullWidth
          label="Operator"
          placeholder="Select Operator"
          options={operatorList}
          getOptionLabel={(opt) => opt.label}
          getOptionValue={(opt) => opt.value}
          value={condition.operator || ''}
          onChange={(e) => onConditionChange(condition.id, 'operator', e.target.value)}
          error={!!operatorError}
          helperText={operatorError}
        />

        {/* Value Input */}
        {renderValueInput()}

        {/* Delete Button */}
        {showDelete && (
          <IconButton
            onClick={() => onRemove(condition.id)}
            sx={{
              color: 'var(--forbrand-error)',
              border: '1px solid #EF4444',
              borderRadius: '4px',
              marginTop: { xs: '0px', md: '33px' },
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
});

const AddRuleSetting = ({ onCancel, onSave, tanMasterId, onChange, formErrors = {}, formData, onNotify = () => { }, onSubmit }) => {

  // State for API response handling
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState({ open: false, message: '', severity: 'success' });
  // Treat any non-null/undefined id as edit mode; allow numeric ids too
  const isEditMode = formData?.id !== null && formData?.id !== undefined && String(formData?.id).trim() !== '';
  const [errors, setErrors] = useState(formErrors);
  const [tdsSectionOptions, setTdsSectionOptions] = useState([]);
  const [conditionTdsSectionOptions, setconditionTdsSectionOptions] = useState([]);

  const { isCollapsed } = useSidebar()

  const [conditions, setConditions] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: 'VENDOR_NAME',
      operator: '',
      value: '',
      tdsSectionOptions: []
    },
  ]);

  // Track conditions marked for deletion (only delete from backend on form submit)
  const [deletedConditions, setDeletedConditions] = useState([]);

  // Fetch tds dropdown options
  const fetchDropdown = async (tanMasterId) => {
    try {
      const response = await getTdsSectionDropdown(tanMasterId);
      if (response && response.data && Array.isArray(response.data.data)) {
        const mappedOptions = response.data.data.map(item => ({
          label: `${item.sectionCode}-${item.broadHead}`,
          value: item.sectionUuid, // value is now the uuid
          sectionCode: item.sectionCode,
          broadHead: item.broadHead,
          id: item.sectionUuid
        }));
        const mappedConditionOptions = response.data.data.map(item => ({
          label: `${item.sectionCode}-${item.broadHead}`,
          value: `${item.sectionCode}-${item.broadHead}`, // value is now the uuid
          sectionCode: item.sectionCode,
          broadHead: item.broadHead,
          id: `${item.sectionCode}-${item.broadHead}`,
        }))
        setTdsSectionOptions(mappedOptions);
        setconditionTdsSectionOptions(mappedConditionOptions)
      } else {
        setTdsSectionOptions([]);
        setconditionTdsSectionOptions([]);
      }
    } catch (err) {
      setTdsSectionOptions([]);
      setconditionTdsSectionOptions([]);
    }
  };

  useEffect(() => {
    if (tanMasterId) {
      fetchDropdown(tanMasterId)
    }
  }, [tanMasterId]);

  // When in edit mode and tdsSection is not set, map mappedTdsSection (label) to its id after options load
  useEffect(() => {
    if (!isEditMode) return;
    if (formData?.tdsSection) return; // already set
    if (!formData?.mappedTdsSection) return;
    if (!tdsSectionOptions.length) return;
    // First try exact label match, then fall back to matching via [old:...] tag in new label format
    let match = tdsSectionOptions.find(opt => opt.label === formData.mappedTdsSection);
    if (!match) {
      const mapped = formData.mappedTdsSection;
      const dashIdx = mapped.indexOf('-');
      const oldSection = dashIdx !== -1 ? mapped.substring(0, dashIdx).trim() : '';
      const oldBroadHead = dashIdx !== -1 ? mapped.substring(dashIdx + 1).trim() : '';
      if (oldSection) {
        match = tdsSectionOptions.find(opt =>
          opt.label?.includes(`[old:${oldSection}]`) && opt.broadHead?.trim() === oldBroadHead
        );
      }
    }
    if (match) {
      onChange({ ...formData, tdsSection: match.id, sectionCode: match.sectionCode || '' });
    }
  }, [isEditMode, formData?.mappedTdsSection, tdsSectionOptions]);

  // Update errors when formErrors prop changes
  useEffect(() => {
    setErrors(formErrors)
  }, [formErrors]);

  // Prefer conditions passed from parent in edit mode; fallback to API fetch when absent
  useEffect(() => {
    // Map conditions already shifted by parent (logicalOperator is on the correct UI row)
    const mapToUi = (arr = []) => arr.map((c, idx) => {
      // c.field may already be a label (parent mapped). If it looks like a code, map to label.
      const isCode = typeof c.field === 'string' && /^[A-Z0-9_]+$/.test(c.field);
      const byCode = fieldOptions.find(o => o.value === c.field);
      const byLabel = fieldOptions.find(o => o.label === c.field);

      return {
        id: c.id || c.conditionId || '',
        backendId: c.conditionId || c.id || '',
        logicalOperator: c.logicalOperator || c.filterOperator || (idx === 0 ? 'Where' : 'And'),
        field: byCode ? byCode.value : (byLabel ? byLabel.value : (c.field || '')),
        operator: c.operator || c.operatorName || '',
        value: c.value || c.conditionDescription || '',
      };
    });

    // Map raw API conditions where logicalOperator on condition N means join between N and N+1.
    // Shift so that condition N+1's UI row displays the operator.
    const mapApiToUi = (arr = []) => arr.map((c, idx) => {
      const fieldKey = c.conditionName || c.field || '';
      const byCode = fieldOptions.find(o => o.value === fieldKey.toUpperCase());
      const byLabel = fieldOptions.find(o => o.label === fieldKey);

      let uiLogicalOperator = 'Where';
      if (idx > 0) {
        const prevApiOp = arr[idx - 1]?.logicalOperator || arr[idx - 1]?.filterOperator || '';
        uiLogicalOperator = prevApiOp.toUpperCase() === 'OR' ? 'OR' : 'And';
      }

      return {
        id: c.id || c.conditionId || '',
        backendId: c.conditionId || c.id || '',
        logicalOperator: uiLogicalOperator,
        field: byLabel ? byLabel.label : (byCode ? byCode.value : (fieldKey || '')),
        operator: (c.operator || c.operatorName || '').toUpperCase(),
        value: c.conditionDescription || c.value || '',
      };
    });

    const init = async () => {
      if (!isEditMode || !formData?.id) return;

      // If parent already provided conditions (already shifted), use them and skip fetch
      if (Array.isArray(formData?.conditions) && formData.conditions.length > 0) {
        setConditions(mapToUi(formData.conditions));
        return;
      }
      try {
        const res = await getRuleConditions(formData.id);
        if (res?.success && Array.isArray(res.data?.data)) {
          // Raw API data — needs shifting
          const mapped = mapApiToUi(res.data.data);
          if (mapped.length) setConditions(mapped);
        }
      } catch (e) {
        // Non-blocking: keep existing conditions on failure
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, formData?.id, Array.isArray(formData?.conditions) ? formData.conditions.length : 0]);

  const mappedFormData = isEditMode ? {
    ...formData,
    startDate: toDisplayDate(formData.startDate),
    endDate: toDisplayDate(formData.endDate),
  } : formData;

  const handleFormChange = (field, value) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    if (field === 'tdsSection' && !isEditMode) {
      // Only update form state, do not call fetchDropdown
      const selectedOption = tdsSectionOptions.find(opt => opt.value === value);
      onChange({
        ...formData,
        tdsSection: value, // value is now the uuid
        sectionCode: selectedOption ? selectedOption.sectionCode : '',
      });
      return;
    }
    onChange({ ...formData, [field]: value });
  };

  const handleConditionChange = useCallback((conditionId, field, value) => {
    setConditions(prevConditions =>
      prevConditions.map(condition => {
        if (condition.id !== conditionId) return condition;

        let updatedCondition = { ...condition };

        // Determine Selected Field Option ---
        const selectedFieldOption = fieldOptions.find(
          opt => opt.value === (field === 'field' ? value : condition.field)
        );

        // Apply Field Transformations ---
        const fieldTransformers = {
          value: val => {
            const uppercaseFields = [
              'ENTITY_TAN',
              'VENDOR_PAN',
              'TIN_UIN',
              'CHALLAN_SERIAL_NO',
              'PROVISION_ADJUSTMENT_REF_NO',
              'INVOICE_NO',
              'ACCOUNTING_DOCUMENT_NO',
              'REFERENCE_NO',
              'HSN_CODE',
              'GL_CODE',
            ];
            if (uppercaseFields.includes(selectedFieldOption?.value)) {
              return String(val).toUpperCase();
            }
            return val;
          },
        };

        // Reset value and validation error when operator changes
        if (field === 'operator' && value !== condition.operator) {
          updatedCondition.value = '';
          setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[`condition_${condition.id}_value`];
            return newErrors;
          });
        }

        updatedCondition[field] = fieldTransformers[field]
          ? fieldTransformers[field](value)
          : value;

        // Rules for Dependent Value Reset ---
        if (field === 'field' && value !== condition.field) {
          updatedCondition.value = '';
          // Reset operator if a numeric/date-only operator is selected but new field doesn't support it
          const newFieldOption = fieldOptions.find(opt => opt.value === value);
          const newDataType = newFieldOption?.dataType || 'string';
          const isNumericOrDate = newDataType === 'number' || newDataType === 'date';
          const numericOnlyOperators = ['BETWEEN', 'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_OR_EQUAL_TO', 'LESS_THAN_OR_EQUAL_TO'];
          if (!isNumericOrDate && numericOnlyOperators.includes(updatedCondition.operator)) {
            updatedCondition.operator = '';
          }
        }

        //  Define Error Clearing Rules ---
        const errorClearRules = {
          field: ['field', 'value', 'operator'],
          operator: ['operator'],
          value: ['value'],
        };

        const errorKeysToClear = (errorClearRules[field] || []).map(
          key => `condition_${condition.id}_${key}`
        );

        // Validation Logic (Re-usable) ---
        const validateFieldValue = (val, fieldMeta, operator) => {
          const valueStr = String(val || '').trim();
          const validation = fieldMeta?.validation;
          const validateForOperator = ['EQUAL_TO', 'NOT_EQUAL_TO'];
          const skipValidationOperators = ['IS_NULL', 'IS_NOT_NULL'];

          // Skip validation for IS_NULL and IS_NOT_NULL operators
          if (skipValidationOperators.includes(condition.operator)) {
            return null;
          }

          // List operators: validate the comma-separated list of values.
          if (LIST_OPERATORS.includes(String(operator).toUpperCase())) {
            return validateCsvListValue(valueStr, fieldMeta);
          }

          // BETWEEN operator: validate two colon-separated values in real-time
          if (String(operator).toUpperCase() === 'BETWEEN') {
            if (!valueStr) return 'Two colon-separated values are required';
            const parts = valueStr.split(':').map(p => p.trim());
            if (parts.length === 2 && parts[0] && parts[1]) {
              const dataType = fieldMeta?.dataType || 'string';
              if (dataType === 'date') {
                const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
                if (!dateRegex.test(parts[0])) return 'First date must be in DD/MM/YYYY format';
                if (!dateRegex.test(parts[1])) return 'Second date must be in DD/MM/YYYY format';
              } else if (dataType === 'number') {
                const numRegex = /^[-+]?\d+(\.\d+)?$/;
                if (!numRegex.test(parts[0])) return 'First value must be a valid number';
                if (!numRegex.test(parts[1])) return 'Second value must be a valid number';
                if (parseFloat(parts[0]) >= parseFloat(parts[1])) return 'First value must be less than the second value';
              }
            }
            return null;
          }

          if (!valueStr) {
            return fieldMeta?.dataType === 'dropdown'
              ? 'Please select a value'
              : 'Value is required';
          }

          if (validation) {
            if (validation.isPanValidation && fieldMeta.label === 'Vendor PAN') {
              const result = validatePAN(valueStr);
              if (!result.isValid) return result.error;
            }

            // Only validate Entity TAN if operator is EQUAL_TO or NOT_EQUAL_TO
            if (validation.isTanValidation && fieldMeta.label === 'Entity TAN') {
              if (validateForOperator.includes(operator)) {
                const result = validateTAN(valueStr);
                if (!result.isValid) return result.error;
              }
            }

            if (validation.maxLength && valueStr.length > validation.maxLength) {
              return validation.error;
            }

            if (validation.regex && !validation.regex.test(valueStr)) {
              return validation.error;
            }
          }

          return null;
        };

        // Update Errors in One Pass ---
        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          // Clear previous errors
          errorKeysToClear.forEach(key => delete newErrors[key]);

          // Field-specific validation
          if (field === 'field' && !updatedCondition.field) {
            newErrors[`condition_${condition.id}_field`] = 'Field is required';
          }

          if (field === 'operator' && !updatedCondition.operator) {
            newErrors[`condition_${condition.id}_operator`] = 'Operator is required';
          }

          if (field === 'value') {
            // For Entity TAN, only validate if operator is EQUAL_TO or NOT_EQUAL_TO
            const validateForOperator = ['EQUAL_TO', 'NOT_EQUAL_TO'];
            if (selectedFieldOption?.label === 'Entity TAN') {
              if (validateForOperator.includes(updatedCondition.operator)) {
                const errorMsg = validateFieldValue(updatedCondition.value, selectedFieldOption, updatedCondition.operator);
                if (errorMsg) newErrors[`condition_${condition.id}_value`] = errorMsg;
              }
            } else if (selectedFieldOption?.label === 'Vendor PAN') {
              if (validateForOperator.includes(updatedCondition.operator)) {
                const errorMsg = validateFieldValue(updatedCondition.value, selectedFieldOption, updatedCondition.operator);
                if (errorMsg) newErrors[`condition_${condition.id}_value`] = errorMsg;
              }
            } else {
              const errorMsg = validateFieldValue(updatedCondition.value, selectedFieldOption, updatedCondition.operator);
              if (errorMsg) newErrors[`condition_${condition.id}_value`] = errorMsg;
            }
          }

          return newErrors;
        });

        return updatedCondition;
      })
    );
  }, []);

  const addCondition = useCallback(() => {
    // Use a functional update so the handler identity stays stable and
    // memoized rows are not forced to re-render when a condition is added.
    setConditions(prev => {
      const newId = Math.max(0, ...prev.map(c => Number(c.id) || 0)) + 1;
      const newCondition = {
        id: newId,
        backendId: '',
        logicalOperator: 'And',
        field: 'VENDOR_NAME',
        operator: '',
        value: '',
      };
      return [...prev, newCondition];
    });
  }, []);

  const removeCondition = useCallback((conditionId) => {
    setConditions(prev => {
      // Only proceed if we have more than 1 condition
      if (prev.length <= 1) return prev;
      const target = prev.find(c => c.id === conditionId);
      if (!target) return prev;

      // If condition has a backendId, mark it for deletion (processed on submit)
      if (target.backendId) {
        setDeletedConditions(d => [...d, target.backendId]);
      }

      const filtered = prev.filter(c => c.id !== conditionId);
      // Ensure the first condition always has 'Where' as logicalOperator
      if (filtered.length > 0) {
        filtered[0] = { ...filtered[0], logicalOperator: 'Where' };
      }
      return filtered;
    });

    // Clear any validation errors for the removed condition
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors[`condition_${conditionId}_field`];
      delete newErrors[`condition_${conditionId}_operator`];
      delete newErrors[`condition_${conditionId}_value`];
      return newErrors;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Rule name required and max length 100
    const ruleName = (formData.ruleName || '').trim();
    if (!ruleName) {
      newErrors.ruleName = 'Rule Name is required';
    } else if (ruleName.length > 100) {
      newErrors.ruleName = 'Rule Name must be at most 100 characters';
    }

    // TDS Section required only when creating (not in edit mode)
    if (!formData.tdsSection && !isEditMode) {
      // Show different error message based on whether TDS sections are available
      if (tdsSectionOptions.length === 0) {
        newErrors.tdsSection = 'Assign TDS Section';
      } else {
        newErrors.tdsSection = 'TDS Section is required';
      }
    }

    // Start date is required; can be past, current, or future
    if (!formData.startDate) {
      newErrors.startDate = 'Start Date is required';
    }

    // End date rules: if provided, must be strictly after start date and a future date
    if (formData.endDate) {
      const startDate = dayjs(formData.startDate, 'DD/MM/YYYY');
      const endDate = dayjs(formData.endDate, 'DD/MM/YYYY');
      const today = dayjs().startOf('day');

      if (!startDate.isValid()) {
        newErrors.startDate = newErrors.startDate || 'Invalid start date';
      }
      if (!endDate.isValid()) {
        newErrors.endDate = 'Invalid end date';
      } else {
        if (!startDate.isValid() || !endDate.isAfter(startDate, 'day')) {
          newErrors.endDate = 'End date must be after start date';
        }
        if (!endDate.isAfter(today, 'day')) {
          newErrors.endDate = 'End date must be a future date';
        }
      }
    }

    // Conditions validation
    conditions.forEach((condition) => {
      if (!condition.field) {
        newErrors[`condition_${condition.id}_field`] = 'Field is required';
      }
      if (!condition.operator) {
        newErrors[`condition_${condition.id}_operator`] = 'Operator is required';
      }

      const op = String(condition.operator).toUpperCase();
      const validateForOperator = ['EQUAL_TO', 'NOT_EQUAL_TO'];
      const skipValidationOperators = ['IS_NULL', 'IS_NOT_NULL'];

      // Skip validation for IS_NULL and IS_NOT_NULL operators
      if (skipValidationOperators.includes(condition.operator)) {
        return null;
      }

      // List operators: validate the comma-separated list of values.
      if (LIST_OPERATORS.includes(op)) {
        const selectedFieldOption = fieldOptions.find(option => option.value === condition.field);
        const listError = validateCsvListValue(condition.value, selectedFieldOption);
        if (listError) {
          newErrors[`condition_${condition.id}_value`] = listError;
        }
        return;
      }

      // BETWEEN operator validation: requires two colon-separated values
      if (op === 'BETWEEN') {
        const valueStr = String(condition.value || '').trim();
        const selectedFieldOption = fieldOptions.find(option => option.value === condition.field);
        const dataType = selectedFieldOption?.dataType || 'string';

        if (!valueStr) {
          newErrors[`condition_${condition.id}_value`] = 'Two colon-separated values are required';
        } else {
          const parts = valueStr.split(':').map(p => p.trim());
          if (parts.length !== 2 || !parts[0] || !parts[1]) {
            newErrors[`condition_${condition.id}_value`] = 'Enter exactly two values separated by a colon (:)';
          } else if (dataType === 'date') {
            const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
            if (!dateRegex.test(parts[0])) {
              newErrors[`condition_${condition.id}_value`] = 'First date must be in DD/MM/YYYY format';
            } else if (!dateRegex.test(parts[1])) {
              newErrors[`condition_${condition.id}_value`] = 'Second date must be in DD/MM/YYYY format';
            } else {
              const startVal = dayjs(parts[0], 'DD/MM/YYYY');
              const endVal = dayjs(parts[1], 'DD/MM/YYYY');
              if (!startVal.isValid() || !endVal.isValid()) {
                newErrors[`condition_${condition.id}_value`] = 'Both dates must be valid';
              } else if (!endVal.isAfter(startVal)) {
                newErrors[`condition_${condition.id}_value`] = 'Second date must be after the first date';
              }
            }
          } else if (dataType === 'number') {
            const numRegex = /^[-+]?\d+(\.\d+)?$/;
            if (!numRegex.test(parts[0])) {
              newErrors[`condition_${condition.id}_value`] = 'First value must be a valid number';
            } else if (!numRegex.test(parts[1])) {
              newErrors[`condition_${condition.id}_value`] = 'Second value must be a valid number';
            } else if (parseFloat(parts[0]) >= parseFloat(parts[1])) {
              newErrors[`condition_${condition.id}_value`] = 'First value must be less than the second value';
            }
          }
        }
        return;
      }

      // Show basic error if value is empty for other operators

      const valueStr = String(condition.value || '').trim();
      // Find the selected field to determine validation config
      const selectedFieldOption = fieldOptions.find(option => option.value === condition.field);
      const validation = selectedFieldOption?.validation;
      if (!valueStr) {
        // Dropdown-specific validation
        if (selectedFieldOption?.dataType === 'dropdown') {
          newErrors[`condition_${condition.id}_value`] = 'Please select a value';
        } else {
          newErrors[`condition_${condition.id}_value`] = 'Value is required';
        }
      } else if (validation) {
        // Check if this is PAN validation
        if (validation.isPanValidation && condition.field === 'VENDOR_PAN') {
          // Only validate Vendor PAN if operator is EQUAL_TO or NOT_EQUAL_TO
          if (validateForOperator.includes(op)) {
            const panValidationResult = validatePAN(valueStr);
            if (!panValidationResult.isValid) {
              newErrors[`condition_${condition.id}_value`] = panValidationResult.error;
            }
          }
        } else if (validation.isTanValidation && condition.field === 'ENTITY_TAN') {
          // Only validate Entity TAN if operator is EQUAL_TO or NOT_EQUAL_TO
          if (validateForOperator.includes(op)) {
            const tanValidationResult = validateTAN(valueStr);
            if (!tanValidationResult.isValid) {
              newErrors[`condition_${condition.id}_value`] = tanValidationResult.error;
            }
          }
        } else {
          // Use validation property for other fields
          if (validation.maxLength && valueStr.length > validation.maxLength) {
            newErrors[`condition_${condition.id}_value`] = validation.error;
          } else if (validation.regex && !validation.regex.test(valueStr)) {
            newErrors[`condition_${condition.id}_value`] = validation.error;
          }
        }
      }
    });

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleCancel = () => {
    // Reset deleted conditions state when cancelling
    setDeletedConditions([]);
    if (onCancel) {
      onCancel();
    }
  };

  // New: delegate submit to parent if provided
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Reuse local validation for UX
    const { isValid, errors: validationErrors } = validateForm();
    if (!isValid) {
      // Surface why the update/save did nothing. With uploaded rules some
      // condition values can exceed field limits; the user needs to know
      // which condition to fix instead of the button silently no-op'ing.
      onNotify({
        message: 'Please fix the highlighted condition errors before saving.',
        severity: 'error',
      });
      // Scroll to the first condition that has an error so it is visible
      // even when the rule has many conditions.
      const firstErrorKey = Object.keys(validationErrors).find(k => k.startsWith('condition_'));
      const firstErrorConditionId = firstErrorKey?.split('_')[1];
      if (firstErrorConditionId) {
        const el = document.getElementById(firstErrorConditionId);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    try {
      setIsSubmitting(true);

      // Process deleted conditions first (only in edit mode)
      // if (isEditMode && deletedConditions.length > 0) {
      //   for (const deletedConditionId of deletedConditions) {
      //     try {
      //       const response = await deleteRuleSettingConditions(deletedConditionId);
      //       if (!response?.success) {
      //         setApiResponse({
      //           open: true,
      //           message: response?.error || `Failed to delete condition with ID: ${deletedConditionId}`,
      //           severity: 'error'
      //         });
      //         return;
      //       }
      //     } catch (error) {
      //       setApiResponse({
      //         open: true,
      //         message: error.message || 'An unexpected error occurred while deleting conditions',
      //         severity: 'error'
      //       });
      //       return;
      //     }
      //   }
      //   // Clear deleted conditions after successful deletion
      //   setDeletedConditions([]);
      // }

      // Proceed with main form submission
      const result = await onSubmit?.({ formData, conditions });
      if (result?.success) {
        onSave?.(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ pb: { xs: '14px', sm: '120px' }, height: '100%', overflowY: 'auto' }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              mb: 0,
            }}
          >
            {/* Header */}
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, fontSize: { xs: '18px', sm: '20px' } }}
            >
              {isEditMode ? 'Edit Rule' : 'Add New Rule'}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: 'var(--forbrand-error)',
              mb: 4,
              fontSize: '14px',
            }}
          >
            (*) Fields are mandatory
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              {/* Rule Basic Information */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                    mb: 3,
                  }}
                >
                  {/* Rule Name */}
                  <Box sx={{ flex: 1 }}>
                    <CustomInput
                      label="Rule Name"
                      required
                      value={mappedFormData.ruleName}
                      onChange={(e) => handleFormChange('ruleName', e.target.value)}
                      error={!!errors.ruleName}
                      helperText={errors.ruleName}
                      placeholder="Enter rule name"
                      maxLength={100}
                      disabled={isEditMode}
                    />
                  </Box>

                  {/* Start Date */}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%'
                    }}>
                      Start Date<span style={{ color: 'var(--forbrand-color)' }}>*</span>
                    </Typography>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={mappedFormData.startDate ? dayjs(mappedFormData.startDate, 'DD/MM/YYYY') : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          handleFormChange('startDate', newValue.format('DD/MM/YYYY'));
                        }
                      }}
                      slotProps={{
                        ...getDatePickerSlotProps({
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          formHelperText: { sx: { margin: 0 } },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '2px',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.startDate ? 'var(--forbrand-error)' : '#d0d0d0',
                              },
                              '&:hover fieldset': {
                                borderColor: errors.startDate ? 'var(--forbrand-error)' : '#a0a0a0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors.startDate ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                              },
                            },
                            '& .MuiFormHelperText-root': {
                              backgroundColor: 'transparent',
                              margin: 0
                            },
                          }
                        })
                      }}
                      maxDate={dayjs()}
                    />
                  </Box>

                  {/* End Date */}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%'
                    }}>
                      End Date
                    </Typography>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={mappedFormData.endDate ? dayjs(mappedFormData.endDate, 'DD/MM/YYYY') : null}
                      onChange={(newValue) => {
                        if (newValue) {
                          handleFormChange('endDate', newValue.format('DD/MM/YYYY'));
                        }
                      }}
                      minDate={
                        (() => {
                          const start = formData.startDate ? dayjs(formData.startDate, 'DD/MM/YYYY').add(1, 'day') : null;
                          const future = dayjs().add(1, 'day');
                          return start && start.isAfter(future) ? start : future;
                        })()
                      }
                      slotProps={{
                        ...getDatePickerSlotProps({
                          error: !!errors.endDate,
                          helperText: errors.endDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '2px',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.endDate ? 'var(--forbrand-error)' : '#d0d0d0',
                              },
                              '&:hover fieldset': {
                                borderColor: errors.endDate ? 'var(--forbrand-error)' : '#a0a0a0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors.endDate ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                              },
                            },
                            '& .MuiFormHelperText-root': {
                              backgroundColor: 'transparent',
                              margin: 0
                            },
                          }
                        })
                      }}
                    />
                  </Box>

                  {/* Active Toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                      Active
                    </Typography>
                    <StyledSwitch
                      checked={!!mappedFormData.active}
                      onChange={(e) => handleFormChange('active', e.target.checked)}
                    />
                  </Box>
                </Box>
              </Box>
            </LocalizationProvider>

            {/* Conditions Section */}
            <Paper sx={{ p: { xs: 2, sm: 2 }, mb: 2, backgroundColor: '#FAFAFA' }} elevation={0}>
              <Box sx={{ mb: 4 }}>
                {conditions.map((condition, index) => (
                  <ConditionRow
                    key={condition.id}
                    condition={condition}
                    index={index}
                    showDelete={conditions.length > 1}
                    fieldError={errors[`condition_${condition.id}_field`]}
                    operatorError={errors[`condition_${condition.id}_operator`]}
                    valueError={errors[`condition_${condition.id}_value`]}
                    conditionTdsSectionOptions={conditionTdsSectionOptions}
                    onConditionChange={handleConditionChange}
                    onRemove={removeCondition}
                  />
                ))}
              </Box>
            </Paper>

            {/* Add Condition Button */}
            <Button
              startIcon={<AddIcon />}
              onClick={addCondition}
              sx={{
                color: '#EF4444',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                mb: 1
              }}
            >
              Add Condition
            </Button>


            {/* TDS Section */}
            <Box sx={{ mb: 6 }}>
              <CustomSelect
                
                label="TDS Section"
                placeholder={tdsSectionOptions.length === 0 ? 'No TDS Sections Available' : 'Select TDS Section'}
                options={tdsSectionOptions}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.id}
                value={mappedFormData.tdsSection || ''}
                onChange={(e) => handleFormChange('tdsSection', e.target.value)}
                disabled={isEditMode || tdsSectionOptions.length === 0}
                error={!!errors.tdsSection}
                helperText={errors.tdsSection}
                sx={{
                  width: { xs: '100%', md: '40%' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: errors.tdsSection ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                  },
                }}
              />
            </Box>
            {/* Action Buttons */}
            <Box sx={{
              position: 'fixed',
              bottom: 0,
               left: { xs: 0, lg: isCollapsed ? '66px' : '300px' },
              right: 0,
              backgroundColor: '#fff',
              boxShadow: {
                xs: '0 -4px 20px rgba(0,0,0,0.15)',
                sm: '0 -2px 10px rgba(0,0,0,0.1)',
              },
              borderTop: '1px solid #e0e0e0',
              zIndex: 1,
              transition: 'left 0.3s ease',
              maxHeight: { xs: '200px', sm: 'auto' },
              overflowY: { xs: 'auto', sm: 'visible' },
            }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end',
                  px: { xs: 2, sm: 3 },
                  py: 2,
                  // height: { xs: 'auto', lg: '64px' },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    minWidth: { xs: 'auto', sm: 100 },
                    flex: { xs: 1, sm: 'none' },
                    color: 'var(--forbrand-primary)',
                    height: { xs: '44px', sm: '40px' },
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: { xs: 500, sm: 400 },
                    border: { xs: '1px solid var(--forbrand-color)', sm: 'none' },
                    borderRadius: { xs: '8px', sm: '4px' },

                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    minWidth: { xs: 'auto', sm: 100 },
                    flex: { xs: 1, sm: 'none' },
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                    height: { xs: '44px', sm: '40px' },
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: { xs: 500, sm: 400 },
                    borderRadius: { xs: '8px', sm: '4px' },
                    boxShadow: {
                      xs: '0 2px 8px rgba(217, 57, 84, 0.3)',
                      sm: '0 2px 4px rgba(0,0,0,0.1)',
                    },

                    '&:disabled': {
                      backgroundColor: '#ccc',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {isEditMode ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
        {/* Snackbar for API response messages */}
        <Snackbar
          open={apiResponse.open}
          autoHideDuration={6000}
          onClose={() => setApiResponse(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setApiResponse(prev => ({ ...prev, open: false }))}
            severity={apiResponse.severity}
            sx={{ width: '100%' }}
          >
            {apiResponse.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default AddRuleSetting;
